import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguageData } from "../contexts/LanguageContext";
import Header from "./Header";
import SideNav from "./SideNav";
import Footer from "./Footer";
import SelectionModal from "./SelectionModal";
import ProficiencyModal from "./ProficiencyModal";
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import "../global.css";
import "./Home.css";

const Home = ({ user }) => {
  const [language, setLanguage] = useState("");
  const [proficiencyLevel, setProficiencyLevel] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showLevelDropdown, setShowLevelDropdown] = useState(false);
  const [showProficiencyModal, setShowProficiencyModal] = useState(false);
  const [showLanguageFirstMessage, setShowLanguageFirstMessage] = useState(false);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const navigate = useNavigate();

  usePageTimeTracker('home', user);

  // Use shared language data from context
  const {
    languages: languagesData,
    proficiencyLevels: levelsData,
    loading,
    fetchLevelsForLanguage
  } = useLanguageData();

  // Fetch levels when language changes
  React.useEffect(() => {
    if (language) {
      setLevelsLoading(true);
      fetchLevelsForLanguage(language).then(() => {
        setLevelsLoading(false);
      }).catch(() => {
        setLevelsLoading(false);
      });
      // Reset proficiency level when language changes
      setProficiencyLevel("");
    }
  }, [language, fetchLevelsForLanguage]);

  // Transform data for dropdown format
  const languages = Object.entries(languagesData).map(([code, data]) => ({
    value: code,
    label: data.label,
    flag: data.flag || `/images/flag-${code}.svg`,
    active: data.active !== false, // Default to true if not specified
  }));

  // Only show proficiency levels if a language is selected
  const proficiencyLevels = language && levelsData && Object.keys(levelsData).length > 0
    ? Object.entries(levelsData).map(([key, data]) => ({
        value: key,
        label: typeof data === 'string' ? data : data.label,
        active: typeof data === 'object' ? data.active !== false : true, // Default to true
      }))
    : [];

  const selectedLanguage = languages.find((lang) => lang.value === language);
  const selectedLevel = proficiencyLevels.find(
    (level) => level.value === proficiencyLevel,
  );

  const handleGetStarted = () => {
    if (language && proficiencyLevel) {
      console.log("Starting with:", { language, proficiencyLevel });
      navigate("/explore-learning", { state: { language, proficiencyLevel } });
    } else {
      setShowModal(true);
    }
  };

  const handleNavigation = (path) => {
    if (language && proficiencyLevel) {
      navigate(path, { state: { language, proficiencyLevel } });
    } else {
      setShowModal(true);
    }
  };

  const handleLanguageSelect = (lang) => {
    if (lang.active) {
      setLanguage(lang.value);
      setShowLanguageDropdown(false);
    }
  };

  const handleLevelSelect = (level) => {
    if (level.active) {
      setProficiencyLevel(level.value);
      setShowLevelDropdown(false);
    }
  };

  if (loading) {
    return (
      <div className="home">
        <Header user={user} />
        <SideNav onNavigate={handleNavigation} />
        <main className="home-content">
          <div className="loading">Loading...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="home">
      <Header user={user} />
      <SideNav onNavigate={handleNavigation} />

      <main className="home-content">
        <div className="illustration-container">
          <img
            src="/images/illustration-complete.png"
            alt="Language learning illustration"
            className="illustration-image"
          />
        </div>

        <div className="content-section">
          <p className="subtitle">Learning & Talent Transformation</p>
          <h1 className="title">
            Welcome to Language Buddy - your trusted partner in mastering
            foreign languages
          </h1>

          <p className="description">
            Choose your language and proficiency level
          </p>

          <div className="form-section">
            <div className="form-field">
              <label htmlFor="language">
                <span className="required">*</span> Language
              </label>
              <div
                className="custom-select-wrapper"
                tabIndex={0}
                onBlur={() => setShowLanguageDropdown(false)}
              >
                <div
                  className="custom-select-trigger"
                  onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                >
                  {selectedLanguage ? (
                    <div className="selected-option">
                      <img
                        src={selectedLanguage.flag}
                        alt=""
                        className="flag-icon"
                      />
                      <span>{selectedLanguage.label}</span>
                    </div>
                  ) : (
                    <span className="placeholder">Select Language</span>
                  )}
                  <img
                    src="/images/dropdownarrow.svg"
                    alt="Dropdown"
                    className={`select-arrow-icon ${showLanguageDropdown ? "open" : ""}`}
                  />
                </div>
                {showLanguageDropdown && (
                  <div className="custom-select-dropdown">
                    {languages.map((lang) => (
                      <div
                        key={lang.value}
                        className={`custom-select-option ${!lang.active ? 'inactive' : ''}`}
                        onClick={() => handleLanguageSelect(lang)}
                      >
                        <img src={lang.flag} alt="" className="flag-icon" />
                        <span>{lang.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="proficiency">
                <span className="required">*</span> Proficiency Level
                <img
                  src="/images/Info.svg"
                  alt="Info"
                  className="info-icon"
                  onClick={() => setShowProficiencyModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowProficiencyModal(true);
                    }
                  }}
                />
              </label>
              <div
                className="custom-select-wrapper"
                tabIndex={0}
                onBlur={() => setShowLevelDropdown(false)}
              >
                <div
                  className="custom-select-trigger"
                  onClick={() => {
                    if (!language) {
                      setShowLanguageFirstMessage(true);
                      setTimeout(() => setShowLanguageFirstMessage(false), 3000);
                    } else if (!levelsLoading) {
                      setShowLevelDropdown(!showLevelDropdown);
                    }
                  }}
                >
                  {levelsLoading ? (
                    <span className="placeholder">Loading levels...</span>
                  ) : selectedLevel ? (
                    <div className="selected-option">
                      <span>{selectedLevel.label}</span>
                    </div>
                  ) : (
                    <span className="placeholder">Select level</span>
                  )}
                  <img
                    src="/images/dropdownarrow.svg"
                    alt="Dropdown"
                    className={`select-arrow-icon ${showLevelDropdown ? "open" : ""}`}
                  />
                </div>
                {showLevelDropdown && !levelsLoading && (
                  <div className="custom-select-dropdown">
                    {proficiencyLevels.map((level) => (
                      <div
                        key={level.value}
                        className={`custom-select-option ${!level.active ? 'inactive' : ''}`}
                        onClick={() => handleLevelSelect(level)}
                      >
                        <span>{level.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {showLanguageFirstMessage && (
                <div className="language-first-warning">
                  Please select a language first
                </div>
              )}
            </div>

            <button
              className={`get-started-btn ${language && proficiencyLevel ? "active" : ""}`}
              onClick={handleGetStarted}
            >
              Get Started
              <img
                src="/images/arrow-right.svg"
                alt=""
                className="arrow-icon"
              />
            </button>
          </div>
        </div>
      </main>

      <Footer />

      <SelectionModal isOpen={showModal} onClose={() => setShowModal(false)} />
      <ProficiencyModal
        isOpen={showProficiencyModal}
        onClose={() => setShowProficiencyModal(false)}
      />
    </div>
  );
};

export default Home;
